/* 편집 단계 정의 — 종류마다 필수 묶음 + 선택 묶음. 왼쪽 메뉴·이전/다음·제출 검사가 이 표 하나를 쓴다. 기획 §14 */
import type {
  Character,
  Chat,
  Command,
  Start,
  StatusBoard,
  World,
} from '@/features/studio-renew/types'

/** 재료(캐릭터 · 세계관 · 상태창 · 명령어) 편집 + 작품 등록(제출) */
export type EditorKind = 'character' | 'world' | 'status' | 'command' | 'work'
export type Ctx = {
  chat?: Chat
  character?: Character
  world?: World
  status?: StatusBoard
  command?: Command
  characters: Record<string, Character>
}
type StepDef = {
  id: string
  label: string
  required: boolean
  done: (ctx: Ctx) => boolean
}

const filled = (v?: string | null) => !!v && v.trim().length > 0

export const profileDone = (c?: Character) =>
  !!c && filled(c.image) && filled(c.name) && filled(c.tagline)
const personaDone = (c?: Character) => !!c && filled(c.persona)
/** 서버 심사 조건 — 시작 장면마다 제목과 캐릭터 대사가 필요하다. */
export const startDone = (s?: Start) =>
  !!s &&
  filled(s.name) &&
  s.intro.lines.some((l) => l.who === 'char' && filled(l.text))
/** 작품 인트로 필수 — 시작이 하나 이상이고 모두 제목·캐릭터 대사가 있다. */
export const startsDone = (chat?: Chat) =>
  !!chat && chat.starts.length > 0 && chat.starts.every(startDone)
/** 작품에 넣을 수 있는 캐릭터 — 필수 두 단계(프로필 · 설정)를 다 채운 것. 인트로는 작품 쪽 */
export const characterReady = (c?: Character) =>
  profileDone(c) && personaDone(c)

/** 작품의 캐릭터 — 1~6명(모두 완성)과 선택한 인물 중 주인공. 작품 이름은 공개 정보에서 받는다. */
const castDone = (x: Ctx) => {
  const chat = x.chat
  if (
    !chat ||
    !chat.characterIds.length ||
    chat.characterIds.length > 6 ||
    !chat.lead ||
    !chat.characterIds.includes(chat.lead)
  )
    return false
  return chat.characterIds.every((id) => characterReady(x.characters[id]))
}
const introDone = (chat?: Chat) =>
  !!chat &&
  (chat.characterIds.length <= 1 || chat.name.trim().length >= 2) &&
  filled(chat.description) &&
  chat.tags.length > 0
const settingsDone = (chat?: Chat) => !!chat && !!chat.audience && chat.consent

export const STEPS: Record<EditorKind, StepDef[]> = {
  character: [
    {
      id: 'profile',
      label: '이름과 모습',
      required: true,
      done: (x) => profileDone(x.character),
    },
    {
      id: 'persona',
      label: '성격과 말투',
      required: true,
      done: (x) => personaDone(x.character),
    },
    {
      id: 'habits',
      label: '말버릇',
      required: false,
      done: (x) => !!x.character?.habits.some((h) => filled(h.text)),
    },
    // 캐릭터 자체의 상황 · 표정 이미지(2026-10-07) — 작품의 컬렉션(장소 → 장면)과는 따로다
    {
      id: 'scenes',
      label: '상황 이미지',
      required: false,
      done: (x) => !!x.character?.album?.some((s) => filled(s.image)),
    },
    {
      id: 'world',
      label: '세계관',
      required: false,
      done: (x) => !!x.character && x.character.worldMode !== 'none',
    },
  ],
  world: [
    {
      id: 'basic',
      label: '기본',
      required: true,
      done: (x) => !!x.world && x.world.name.trim().length >= 2,
    },
    {
      id: 'body',
      label: '세계관 설정',
      required: true,
      done: (x) => filled(x.world?.body),
    },
    {
      id: 'keywords',
      label: '키워드북',
      required: false,
      done: (x) => !!x.world?.keywords.length,
    },
    {
      id: 'visibility',
      label: '공개 범위',
      required: true,
      done: (x) => !!x.world,
    },
  ],
  // 상태창 · 명령어는 한 화면짜리 재료 — 작품에는 연결만 한다(2차 결정)
  status: [
    {
      id: 'edit',
      label: '상태창',
      required: true,
      done: (x) =>
        !!x.status &&
        x.status.name.trim().length > 0 &&
        x.status.fields.length > 0,
    },
  ],
  command: [
    {
      id: 'edit',
      label: '명령어',
      required: true,
      done: (x) =>
        !!x.command &&
        /^![^\s!]{1,49}$/.test(x.command.key) &&
        filled(x.command.title),
    },
  ],
  work: [
    {
      id: 'intro',
      label: '소개',
      required: true,
      done: (x) => introDone(x.chat),
    },
    { id: 'cast', label: '캐릭터', required: true, done: castDone },
    {
      id: 'album',
      label: '프롬프트',
      required: false,
      done: (x) =>
        filled(x.chat?.situation) ||
        (!!x.chat?.albumOn && x.chat.albumPlaces.some((p) => p.scenes.length)),
    },
    {
      id: 'starts',
      label: '시작 장면',
      required: true,
      done: (x) => startsDone(x.chat),
    },
    {
      id: 'tools',
      label: '플러그인',
      required: false,
      done: (x) =>
        !!x.chat &&
        (!!x.chat.statusId ||
          x.chat.commandIds.length + x.chat.officialCommands.length > 0),
    },
    {
      id: 'publish',
      label: '설정',
      required: true,
      done: (x) => settingsDone(x.chat),
    },
  ],
}

export const KIND_LABEL: Record<EditorKind, string> = {
  character: '캐릭터',
  world: '세계관',
  status: '상태창',
  command: '명령어',
  work: '작품 등록',
}

/** 테스트는 편집·제출 단계의 진행률에 포함하지 않는 별도 화면이다. */
export const AUTHOR_TEST_STEP: StepDef = {
  id: 'test',
  label: '테스트',
  required: false,
  done: () => false,
}
const WORK_REVIEW_STEP: StepDef = {
  id: 'review',
  label: '최종 확인',
  required: false,
  done: (ctx) =>
    introDone(ctx.chat) &&
    castDone(ctx) &&
    startsDone(ctx.chat) &&
    settingsDone(ctx.chat),
}
export const testLabel = (kind: EditorKind) =>
  kind === 'character' ? '캐릭터 테스트' : '작품 테스트'

export const stepOf = (kind: EditorKind, id: string) =>
  kind === 'work' && id === 'review'
    ? WORK_REVIEW_STEP
    : id === 'test' && (kind === 'character' || kind === 'work')
      ? AUTHOR_TEST_STEP
      : STEPS[kind].find(
          (s) =>
            s.id ===
            (kind === 'work' && (id === 'status' || id === 'commands')
              ? 'tools'
              : id)
        ) || STEPS[kind][0]
export const firstMissing = (kind: EditorKind, ctx: Ctx) =>
  STEPS[kind].find((s) => s.required && !s.done(ctx))

/** 테스트에 빠진 입력 — 어느 단계의 어느 칸(Field 라벨)인지. characterId 가 있으면 그 캐릭터의 편집 창에서 채운다 */
export type TestMissing = {
  message: string
  step: string
  label: string
  startId?: string
  characterId?: string
}

const NEED_NAME = {
  message: '캐릭터 이름을 입력해 주세요.',
  step: 'profile',
  label: '이름',
}
const NEED_PERSONA = {
  message: '캐릭터 설정을 입력해 주세요.',
  step: 'persona',
  label: '캐릭터 설정',
}

function castMemberMissing(character?: Character) {
  if (!filled(character?.image))
    return {
      message: '대표 이미지를 올려 주세요.',
      step: 'profile',
      label: '대표 이미지',
    }
  if (!filled(character?.name)) return NEED_NAME
  if (!filled(character?.tagline))
    return {
      message: '한 줄 소개를 입력해 주세요.',
      step: 'profile',
      label: '한 줄 소개',
    }
  return NEED_PERSONA
}

/** 테스트 대화를 여는 데 꼭 필요한 입력 중 처음 빠진 것 */
export function testMissing(
  kind: 'character' | 'work',
  ctx: Ctx,
  openingId?: string
): TestMissing | null {
  if (kind === 'character') {
    if (!filled(ctx.character?.name)) return NEED_NAME
    return personaDone(ctx.character) ? null : NEED_PERSONA
  }
  const chat = ctx.chat
  if (!chat?.characterIds.length)
    return {
      message: '캐릭터를 추가해 주세요.',
      step: 'cast',
      label: '출연 캐릭터',
    }
  const incomplete = chat.characterIds.find(
    (id) => !characterReady(ctx.characters[id])
  )
  if (incomplete)
    return {
      ...castMemberMissing(ctx.characters[incomplete]),
      characterId: incomplete,
    }
  const start =
    chat.starts.find((item) => item.id === openingId) ?? chat.starts[0]
  if (!filled(start?.name))
    return {
      message: '시작 장면 제목을 입력해 주세요.',
      step: 'starts',
      label: '시작 장면 제목',
      startId: start?.id,
    }
  return startDone(start)
    ? null
    : {
        message: '인트로를 입력해 주세요.',
        step: 'starts',
        label: '인트로',
        startId: start?.id,
      }
}

export const isKind = (value: string): value is EditorKind => value in STEPS

/** 작품 이름 — 비우면 주인공 이름 */
export const chatTitle = (chat: Chat, characters: Record<string, Character>) =>
  chat.name.trim() || characters[chat.lead || '']?.name || '이름 없는 작품'
